import React, { useState } from 'react';
import { Globe, ShieldAlert, ShieldCheck } from 'lucide-react';
import { CodeBlock, CopyButton } from '../render/CodeBlock';
import { HTML_SANDBOX_ATTR, isSandboxIsolated } from '../render/htmlSandbox';
import { ViewToggle } from './DocumentViewers';
import type { ArtifactViewerProps } from '../types';
import type { HtmlContent } from './shared';

// Artefatti HTML come esperienze interattive (Task #3817): script ABILITATI
// in iframe sandbox con origine opaca (srcDoc + `allow-scripts` SENZA
// `allow-same-origin`) → cookie, sessione, localStorage e window parent
// dell'app sono inaccessibili per policy del browser. Guardia fail-closed:
// se il contratto sandbox non è isolato, l'iframe NON viene montato.

export const HtmlViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<HtmlContent>;
  const body = typeof c?.body === 'string' ? c.body : '';
  const [view, setView] = useState<'preview' | 'source'>('preview');
  const sandboxOk = isSandboxIsolated(HTML_SANDBOX_ATTR);

  return (
    <div className="bg-card rounded-lg overflow-hidden border border-border">
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/60">
        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          Anteprima interattiva
          <ShieldCheck className="h-3 w-3 text-emerald-600" aria-label="Sandbox isolata" />
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <CopyButton text={body} className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" />
          <ViewToggle view={view} onChange={setView} />
        </div>
      </div>
      {view === 'source' ? (
        <CodeBlock code={body} language="html" showHeader={false} className="rounded-none border-0" />
      ) : sandboxOk ? (
        <iframe
          title={`html-${artifact.slug}`}
          data-testid="html-artifact-frame"
          srcDoc={body}
          sandbox={HTML_SANDBOX_ATTR}
          referrerPolicy="no-referrer"
          className="w-full h-[420px] bg-white"
        />
      ) : (
        // Fail-closed: contratto sandbox violato → niente iframe, errore visibile.
        <div className="p-4 text-sm text-red-700 bg-red-50 flex items-center gap-2" data-testid="html-sandbox-blocked">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          Anteprima bloccata: configurazione sandbox non sicura.
        </div>
      )}
    </div>
  );
};
